import React, { useState, useRef, useEffect } from 'react';
import { StatusActionButton } from '../../../shared/ui';
import styles from './EditBio.module.css';

interface EditBioProps {
  initialBio?: string;
  onSave: (bio: string) => Promise<void>;
  onCancel: () => void;
}

const EditBio: React.FC<EditBioProps> = ({ initialBio = '', onSave, onCancel }) => {
  const [bioText, setBioText] = useState(initialBio);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Автофокус на textarea при монтировании
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(bioText.length, bioText.length);
    }
  }, []);

  // Обновляем текст при изменении initialBio
  useEffect(() => {
    setBioText(initialBio);
  }, [initialBio]);

  const handleSubmit = async () => {
    if (isSaving) return;

    setIsSaving(true);
    setError(null);
    
    try {
      // Сохраняем текст как есть, чтобы сохранить переносы строк
      // Убираем только пробелы в начале и конце всего текста
      const trimmedBio = bioText.trim();
      await onSave(trimmedBio || '');
      // После успешного сохранения модалка закроется через onSave
    } catch (err: any) {
      console.error('Ошибка сохранения биографии:', err);
      
      let errorMessage = 'Ошибка сохранения биографии';
      
      if (err?.response?.data) {
        const data = err.response.data;
        
        // Обработка стандартных ошибок валидации ASP.NET Core
        if (data.errors && typeof data.errors === 'object') {
          const validationErrors: string[] = [];
          for (const [field, messages] of Object.entries(data.errors)) {
            const msgArray = Array.isArray(messages) ? messages : [messages];
            validationErrors.push(`${field}: ${msgArray.join(', ')}`);
          }
          if (validationErrors.length > 0) {
            errorMessage = `Ошибки валидации:\n${validationErrors.join('\n')}`;
          }
        }
        // Обработка кастомных ошибок валидации
        else if (data.validationErrors && Array.isArray(data.validationErrors)) {
          const validationErrors = data.validationErrors
            .map((err: any) => `${err.Field}: ${err.Errors?.join(', ') || 'Ошибка валидации'}`)
            .join('\n');
          errorMessage = `Ошибки валидации:\n${validationErrors}`;
        } 
        else if (data.error) {
          errorMessage = data.error;
        } 
        else if (data.message) {
          errorMessage = data.message;
        }
        else if (data.title) {
          errorMessage = data.title;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setBioText(initialBio);
    setError(null);
    onCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={styles.editBio}>
      {error && (
        <div className={styles.error}>
          <i className="ri-error-warning-line"></i>
          <span>{error}</span>
        </div>
      )}

      <div className={styles.inputContainer}>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          placeholder="Расскажите о себе..."
          value={bioText}
          onChange={(e) => setBioText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={6}
          maxLength={500}
          disabled={isSaving}
        />
        <div className={styles.charCount}>
          {bioText.length}/500
        </div>
      </div>

      <div className={styles.actions}>
        <StatusActionButton
          variant="secondary"
          onClick={handleCancel}
          disabled={isSaving}
          size="small"
        >
          Отмена
        </StatusActionButton>
        <StatusActionButton
          variant="primary"
          onClick={handleSubmit}
          disabled={isSaving}
          size="small"
        >
          {isSaving ? (
            <>
              <i className="ri-loader-4-line ri-spin"></i>
              Сохранение...
            </>
          ) : (
            'Сохранить'
          )}
        </StatusActionButton>
      </div>
    </div>
  );
};

export default EditBio;
